'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

interface TemplateReview {
  id: string;
  quote: string;
  summary: string | null;
  rating: number;
  featured: boolean;
  created_at: string;
  helpful_count: number;
  name: string;
  role: string;
  avatar: string | null;
  avatarAlt: string;
}

interface TemplateReviewCardProps {
  review: TemplateReview;
}

function TemplateReviewCard({ review }: TemplateReviewCardProps) {
  const [expanded, setExpanded] = useState(false);
  const hasSummary = !!review.summary;
  const displayText = expanded || !hasSummary ? review.quote : review.summary!;

  return (
    <div
      className="rounded-2xl p-5"
      style={{ background: 'var(--bg-card)', border: '1px solid var(--border)' }}>
      <div className="flex items-center gap-0.5 mb-3">
        {[1, 2, 3, 4, 5].map((s) => (
          <Icon
            key={s}
            name="StarIcon"
            size={13}
            variant={s <= review.rating ? 'solid' : 'outline'}
            className={s <= review.rating ? 'star-filled' : ''}
            style={{ color: s <= review.rating ? undefined : 'var(--text-muted)' }}
          />
        ))}
        <span className="ml-2 text-xs" style={{ color: 'var(--text-muted)' }}>
          {new Date(review.created_at).toLocaleDateString()}
        </span>
      </div>
      <blockquote
        className="text-sm leading-relaxed mb-1"
        style={{ color: 'var(--text-secondary)' }}>
        &ldquo;{displayText}&rdquo;
      </blockquote>
      {hasSummary && (
        <button
          onClick={() => setExpanded(!expanded)}
          className="text-xs font-medium mb-3 transition-colors"
          style={{ color: 'var(--accent)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
          {expanded ? 'Show less' : 'Read more…'}
        </button>
      )}
      <div className="flex items-center gap-2 pt-3" style={{ borderTop: '1px solid var(--border)' }}>
        <div
          className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: 'var(--accent-light)', color: 'var(--accent)' }}>
          <Icon name="UserIcon" size={14} />
        </div>
        <div>
          <p className="text-xs font-semibold" style={{ color: 'var(--text-primary)' }}>{review.name}</p>
          <p className="text-xs" style={{ color: 'var(--text-muted)' }}>{review.role}</p>
        </div>
      </div>
    </div>
  );
}

interface TemplateReviewsSectionProps {
  templateId: string;
}

export default function TemplateReviewsSection({ templateId }: TemplateReviewsSectionProps) {
  const [reviews, setReviews] = useState<TemplateReview[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!templateId) return;
    const fetchReviews = async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/reviews/template?template_id=${templateId}&limit=3`);
        if (!res.ok) throw new Error('Failed to fetch');
        const json = await res.json();
        setReviews(json.data || []);
        setTotal(json.total || 0);
      } catch (err) {
        console.error('Failed to load template reviews:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchReviews();
  }, [templateId]);

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2].map((i) => (
          <div key={i} className="h-28 rounded-2xl animate-pulse" style={{ background: 'var(--bg-secondary)' }} />
        ))}
      </div>
    );
  }

  if (reviews.length === 0) return null;

  return (
    <div className="mt-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-display font-semibold text-base" style={{ color: 'var(--text-primary)' }}>
          Customer Reviews
        </h3>
        {total > 3 && (
          <Link
            href={`/reviews?template_id=${templateId}`}
            className="text-xs font-medium transition-colors"
            style={{ color: 'var(--accent)' }}>
            See all {total} reviews →
          </Link>
        )}
      </div>
      <div className="space-y-3">
        {reviews.map((review) => (
          <TemplateReviewCard key={review.id} review={review} />
        ))}
      </div>
      {total > 3 && (
        <div className="mt-4">
          <Link
            href={`/reviews?template_id=${templateId}`}
            className="btn-secondary text-sm w-full text-center block py-2"
          >
            See all reviews for this template
          </Link>
        </div>
      )}
    </div>
  );
}
